// api.js

import { accessToken } from '../hooks/useAccessToken.js';
import { buildUrlWithQueryParameters, saveFile } from './utils.js'

// exceptions bubble up to caller to handle


/****************************************************************************
 * 
 * Application config
 * 
 ***************************************************************************/

/* Needed because hooks (including useConfig) cannot be used outside of components. This function is used to fetch the config passed down to the entire document*/
export const fetchAppConfig = async () => {
    const url = '/config.json'
    let response = await fetch(url)
    let data = await response.json()
    return data
}

/****************************************************************************
 * 
 * Tasks
 * 
 ***************************************************************************/

/**
 * Fetch Task List for a given Business Entity ID
 * @param {string} businessEntityId - The Business Entity ID
 * @param {object} filter - The filter object containing the predicates array
 * @param {object} config - The application config object
 * @param {object} accessToken - The logged in user's accessToken
 * @returns {Promise} - The UTG list as a Promise
 */
export const fetchTaskList = async (businessEntityId, filter, config, accessToken) => {
    const url = `${config.managementServiceBaseUrl}/businessentities/${businessEntityId}/tasks/query`
    const tasksFilter = filter ? JSON.stringify(filter) : '{"Predicates": []}'
    const response = await fetch(url, {
        method: "POST",
        cache: "no-cache",
        mode: "cors",
        referrerPolicy: "origin",
        headers: {
            "Content-Type": "application/json",
            'Authorization': `Token token="${accessToken}"`
        },
        body: JSON.stringify(tasksFilter).replace("\n", "") // make sure we encode the query correctly
    })

    if (!response.ok) {
        let error
        if (response.status == 401) {
            // unauthorized
            error = new Error('Unauthorized: Please login from the old environment.')
            error.code = "401"
        }
        else {
            error = new Error(`Failed to fetch Task List for Business Entity ID: ${businessEntityId}. Status: ${response.statusText}`)
            error.code = response.status
        }
        throw error
    }
    return await response.json();
};

/**
 * Fetch Tasks for a given Business Entity ID and Component ID
 * @param {string} businessEntityId - The Business Entity ID
 * @param {string} componentId - The Component ID
 * @param {object} config - The application config object
 * @param {object} accessToken - The logged in user's accessToken
 * @returns {Promise} - The UTG as a Promise
 */
export const fetchUtgTaskList = async (businessEntityId, componentId, config, accessToken) => {
    const url = `${config.managementServiceBaseUrl}/businessentities/${businessEntityId}/tasks/query`
    const tasksFilter = `{"Predicates": [{"PropertyName":"ComponentId","Operator":"EQ","Value":"${componentId}","TypeHint": "Integer"}]}`
    const response = await fetch(url, {
        method: "POST",
        cache: "no-cache",
        mode: "cors",
        referrerPolicy: "origin",
        headers: {
            "Content-Type": "application/json",
            'Authorization': `Token token="${accessToken}"`
        },
        body: JSON.stringify(tasksFilter).replace("\n", "") // make sure we encode the query correctly
    })
    if (!response.ok) {
        let error
        if (response.status == 401) {
            // unauthorized
            error = new Error('Unauthorized: Please login from the old environment.')
            error.code = "401"
        }
        else {
            error = new Error(`Failed to fetch UTG Tasks for Business Entity ID: ${businessEntityId}. Status: ${response.statusText}`)
            error.code = response.status
        }
        throw error
    }
    return (await response.json());
};


/**
 * Create New Task
 * @param {string} businessEntityId - The Business Entity ID
 * @param {string} machinePermanentId - The Machine Permanent ID
 * @param {object} taskInstructions - The Profile ID
 * @param {object} config - The application config object
 * @param {object} accessToken - The logged in user's accessToken
 * @returns {Promise} - The update result as a Promise
 */
export const CreateTask = async (businessEntityId, machinePermanentId, taskInstructions, successCallback, config, accessToken) => {
    try {

        if (typeof businessEntityId == 'undefined') {
            throw new Error("Undefined business entity ID")
        } else if (typeof machinePermanentId == 'undefined') {
            throw new Error("Undefined machine permanent ID")
        }

        const url = `${config.managementServiceBaseUrl}/businessentities/${businessEntityId}/machines/${machinePermanentId}/tasks`

        // IMPORTANT: The Shift4 envelope headers are required by the REST api to properly query the data. Also note that this is PUT rather than POST.
        const response = await fetch(url, {
            method: "POST",
            cache: "no-cache",
            mode: "cors",
            referrerPolicy: "origin",
            headers: {
                "Content-Type": "application/json",
                'Authorization': `Token token="${accessToken}"`,
                'Shift4-Rest-Request-Envelope-Version': '1',
                'Shift4-Rest-Response-Envelope-Version': '1',
                'Clientid': '4'
            },
            body: JSON.stringify(taskInstructions)
        })

        if (!response.ok) {
            let error
            if (response.status == 401) {
                // unauthorized
                error = new Error('Unauthorized: Please login from the old environment.')
                error.code = "401"
            }
            else {
                error = new Error(`Failed to create task. Status: ${response.statusText}`)
                error.code = response.status
            }
            throw error
        }
        else {
            if (typeof successCallback == 'function') {
                successCallback()
            }
        }
        return await response.json();
    } catch (x) {
        console.trace(x.toString())
        window.alert("CreateTask exception: "+x.toString())
    }
}

export const CreateTasks = async (businessEntityId, taskInstructions, successCallback, errorCallback, config, accessToken) => {
    const f = "CreateTasks()"
    const print = false
    try {
        if (print) {
            console.log(f.concat(": entered with the following task instructions:"))
            console.log(taskInstructions)
        }
        if (typeof businessEntityId == 'undefined') {
            throw new Error("Undefined business entity ID")
        }
        const url = `${config.managementServiceBaseUrl}/businessentities/${businessEntityId}/tasksbulk`
        const response = await fetch(url, {
            method: "POST",
            cache: "no-cache",
            mode: "cors",
            referrerPolicy: "origin",
            headers: {
                "Content-Type": "application/json",
                'Authorization': `Token token="${accessToken}"`,
                'Shift4-Rest-Request-Envelope-Version': '1',
                'Shift4-Rest-Response-Envelope-Version': '1',
                'Clientid': '4'
            },
            body: JSON.stringify(taskInstructions)
        })
        if (!response.ok) {
            let error
            if (response.status == 401) {
                // unauthorized
                error = new Error('Unauthorized: Please login from the old environment.')
                error.code = "401"
            }
            else {
                error = new Error(`Failed to create task. Status: ${response.statusText}`)
                error.code = response.status
            }
            throw error
        }
        else {
            if (print) {
                console.log(f.concat(": successfully created multiple tasks"))
            }
            if (typeof successCallback == 'function') {
                if (print) {
                    console.log(f.concat(": about to execute success callback"))
                }
                successCallback()
            }
            else if(print){
                console.log(f.concat(": success callback is not a function"))
            }
        }
        return await response.json();
    } catch (x) {
        if (typeof errorCallback == 'function') {
            errorCallback(x)
        }
        else {
            if (print) {
                console.log(f.concat(": error callback is not a function"))
            }
            console.trace(x.toString())
            window.alert("CreateTasks exception: " + x.toString())
            throw x
        }
    }
}

/**
 * Delete Task
 * @param {string} businessEntityId - The Business Entity ID
 * @param {string} machinePermanentId - The Machine Permanent ID
 * @param {object} taskId - The Task ID
 * @param {object} config - The application config object
 * @param {object} accessToken - The logged in user's accessToken
 * @returns {Promise} - The update result as a Promise
 */
export const DeleteTask = async (businessEntityId, machinePermanentId, taskId, config, accessToken) => {
    const url = `${config.managementServiceBaseUrl}/businessentities/${businessEntityId}/machines/${machinePermanentId}/tasks/${taskId}`

    // IMPORTANT: The Shift4 envelope headers are required by the REST api. Also note the server requires POST with a header override to indicate DELETE
    const response = await fetch(url, {
        method: "POST",
        cache: "no-cache",
        mode: "cors",
        referrerPolicy: "origin",
        headers: {
            'X-HTTP-Method-Override': 'DELETE',
            'Authorization': `Token token="${accessToken}"`,
            'Shift4-Rest-Request-Envelope-Version': '1',
            'Shift4-Rest-Response-Envelope-Version': '1',
            'Clientid': '4'
        }
    })

    if (!response.ok) {
        let error
        if (response.status == 401) {
            // unauthorized
            error = new Error('Unauthorized: Please login from the old environment.')
            error.code = "401"
        }
        else {
            error = new Error(`Failed to delete task. Status: ${response.statusText}`)
            error.code = response.status
        }
        throw error
    }
    return response;
};

/**
 * Export Tasks
 * @param {string} businessEntityId - The Business Entity ID
 * @param {object} config - The application config object
 * @param {object} accessToken - The logged in user's accessToken
 * @returns {Promise} - The update result as a Promise
 */
export const ExportTasks = async (businessEntityId, config, accessToken) => {
    const url = `${config.managementServiceBaseUrl}/businessentities/${businessEntityId}/tasks/query/csv`
    const tasksFilter = '{"Predicates": []}'
    // IMPORTANT: The Shift4 envelope headers are required by the REST api to properly query the data. Also note that this is PUT rather than POST.
    const response = await fetch(url, {
        method: "POST",
        cache: "no-cache",
        mode: "cors",
        referrerPolicy: "origin",
        headers: {
            "Content-Type": "application/json",
            'Authorization': `Token token="${accessToken}"`,
            'Shift4-Rest-Request-Envelope-Version': '1',
            'Shift4-Rest-Response-Envelope-Version': '1',
            'Clientid': '4'
        },
        body: JSON.stringify(tasksFilter).replace("\n", "") // make sure we encode the query correctly
    })

    if (!response.ok) {
        let error
        if (response.status == 401) {
            // unauthorized
            error = new Error('Unauthorized: Please login from the old environment.')
            error.code = "401"
        }
        else {
            error = new Error(`Failed to export tasks. Status: ${response.statusText}`)
            error.code = response.status
        }
        throw error
    }
    return await response.arrayBuffer()
}

/****************************************************************************
 * 
 * Devices
 * 
 ***************************************************************************/

/**
 * Fetch Device List for a given Business Entity ID
 * @param {string} businessEntityId - The Business Entity ID
 * @param {object} filters - Any filters that need to be applied to data
 * @param {object} config - The application config object
 * @param {object} accessToken - The logged in user's accessToken
 * @returns {Promise} - The UTG list as a Promise
 */
export const fetchDeviceList = async (businessEntityId, filters, config, accessToken) => {
    const url = `${config.managementServiceBaseUrl}/businessentities/${businessEntityId}/devices/query`
    const devicesFilter = filters ? JSON.stringify(filters) : '{"Predicates": []}';
    const response = await fetch(url, {
        method: "POST",
        cache: "no-cache",
        mode: "cors",
        referrerPolicy: "origin",
        headers: {
            "Content-Type": "application/json",
            'Authorization': `Token token="${accessToken}"`
        },
        body: JSON.stringify(devicesFilter).replace("\n", "") // make sure we encode the query correctly
    })

    if (!response.ok) {
        let error
        if (response.status == 401) {
            // unauthorized
            error = new Error('Unauthorized: Please login from the old environment.')
            error.code = "401"
        }
        else {
            error = new Error(`Failed to fetch Device List for Business Entity ID: ${businessEntityId}. Status: ${response.statusText}`)
            error.code = response.status
        }
        throw error
    }
    return await response.json();
};

/**
 * Export Devices List
 * XXX This is exactly the same as fetchDeviceList except the return value is an array buffer instead of JSON
 * @param {string} businessEntityId - The Business Entity ID
 * @param {object} config - The application config object
 * @param {object} accessToken - The logged in user's accessToken
 * @returns {Promise} - The update result as a Promise
 */
export const ExportDeviceList = async (businessEntityId, config, accessToken) => {
    const url = `${config.managementServiceBaseUrl}/businessentities/${businessEntityId}/devices/query/csv`
    const tasksFilter = '{"Predicates": []}'
    // IMPORTANT: The Shift4 envelope headers are required by the REST api to properly query the data. Also note that this is PUT rather than POST.
    const response = await fetch(url, {
        method: "POST",
        cache: "no-cache",
        mode: "cors",
        referrerPolicy: "origin",
        headers: {
            "Content-Type": "application/json",
            'Authorization': `Token token="${accessToken}"`,
            'Shift4-Rest-Request-Envelope-Version': '1',
            'Shift4-Rest-Response-Envelope-Version': '1',
            'Clientid': '4'
        },
        body: JSON.stringify(tasksFilter).replace("\n", "") // make sure we encode the query correctly
    })

    if (!response.ok) {
        let error
        if (response.status == 401) {
            // unauthorized
            error = new Error('Unauthorized: Please login from the old environment.')
            error.code = "401"
        }
        else {
            error = new Error(`Failed to export tasks. Status: ${response.statusText}`)
            error.code = response.status
        }
        throw error
    }
    return await response.arrayBuffer()
}

/**
 * Fetch Devices for a given Business Entity ID and Component ID
 * @param {string} businessEntityId - The Business Entity ID
 * @param {string} componentId - The Component ID
 * @param {object} config - The application config object
 * @param {object} accessToken - The logged in user's accessToken
 * @returns {Promise} - The UTG as a Promise
 */
export const fetchUtgDeviceList = async (businessEntityId, componentId, config, accessToken) => {
    const url = `${config.managementServiceBaseUrl}/businessentities/${businessEntityId}/devices/query`
    const tasksFilter = `{"Predicates": [{"PropertyName":"ComponentId","Operator":"EQ","Value":"${componentId}","TypeHint": "Integer"}]}`
    const response = await fetch(url, {
        method: "POST",
        cache: "no-cache",
        mode: "cors",
        referrerPolicy: "origin",
        headers: {
            "Content-Type": "application/json",
            'Authorization': `Token token="${accessToken}"`
        },
        body: JSON.stringify(tasksFilter).replace("\n", "") // make sure we encode the query correctly
    })
    if (!response.ok) {
        let error
        if (response.status == 401) {
            // unauthorized
            error = new Error('Unauthorized: Please login from the old environment.')
            error.code = "401"
        }
        else {
            error = new Error(`Failed to fetch UTG Devices for Business Entity ID: ${businessEntityId}. Status: ${response.statusText}`)
            error.code = response.status
        }
        throw error
    }
    return (await response.json());
};

/**
 * Fetch device forms for a given business entity
 * @param {string} businessEntityId - The Business Entity ID
 * @param {object} config - The application config object
 * @param {object} accessToken - The logged in user's accessToken
 * @returns {Promise} - The Device Forms as a Promise
 */
export const fetchDeviceForms = async (businessEntityId, config, accessToken) => {
    const url = `${config.managementServiceBaseUrl}/businessentities/${businessEntityId}/componentDeviceForms`
    const response = await fetch(url, {
        method: "GET",
        headers: {
            "Content-Type": "application/json",
            'Authorization': `Token token="${accessToken}"`,
            'Shift4-Rest-Request-Envelope-Version': '1',
            'Shift4-Rest-Response-Envelope-Version': '1'
        }
    })
    if (!response.ok) {
        let error
        if (response.status == 401) {
            // unauthorized
            error = new Error('Unauthorized: Please login from the old environment.')
            error.code = "401"
        }
        else {
            error = new Error(`Failed to fetch Device Forms: ${businessEntityId}. Status: ${response.statusText}`)
            error.code = response.status
        }
        throw error
    }
    return await response.json().then((item) => { return item.componentDeviceForm })
};

export const uploadFileToPresignedURL = async function (file, url, successCallback, errorCallback) {
    const f = "uploadFileToPresignedURL"
    if (typeof successCallback != 'function') {
        throw new Error(f.concat(": this function expects a success callback"))
    }
    console.log(f.concat(": about to fetch from url \"").concat(url).concat("\""))
    const response = await fetch(url, {
        method: 'PUT',
        headers: {
            //'Access-Control-Allow-Headers': "Accept, Content-Type, Origin, X-Requested-With",
            //'Access-Control-Allow-Allow-Methods': "GET, POST, PUT",
            //'Access-Control-Allow-Origin': '*',
            'Content-Type': file.type
        },
        body: file
    })
    if (response.ok) {
        console.log(f.concat(": Successfully uploaded file to presigned URL"))
        if (typeof successCallback == 'function') {
            console.log(f.concat(": about to execute success callback"))
            successCallback()
        }
        else {
            console.log(f.concat(": success callback is not a function"))
        }
    }
    else {
        if (typeof errorCallback == 'function') {
            console.log(f.concat(": about to execute error callback"))
            errorCallback();
        }
        else {
            console.log(f.concat(": error callback is not a function"))
        }
        const error = new Error(`Failed to upload file to presigned URL. Status: ${response.statusText}`)
        error.code = response.status
        throw error
    }
}

export const UpdateDeviceFormUploadStatus = async function (businessEntityId, formId, isZipFile, successCallback, config, accessToken) {
    const f = "UpdateDeviceFormUploadStatus"
    const url = `${config.managementServiceBaseUrl}/businessentities/${businessEntityId}/componentDeviceForms/${formId}/uploaded?iszipfile=${isZipFile}`
    fetch(url, {
        method: 'PUT',
        cache: 'no-cache',
        mode: 'cors',
        referrerPolicy: 'origin',
        headers: {
            "Content-Type": "application/json",
            'Authorization': `Token token="${accessToken}"`,
            'Shift4-Rest-Request-Envelope-Version': '1',
            'Shift4-Rest-Response-Envelope-Version': '1',
            'Clientid': '4'
        }
    }).then(async (response) => {
        console.log(f.concat(": got a response from server"))
        if (response.ok) {
            console.log(f.concat(": response is OK. Successfully marked form ").concat(formId).concat(" as uploaded"))
            console.log(response)
            if (typeof successCallback == 'function') {
                console.log(f.concat(": about to execute success callback"))
                successCallback();
            }
            else {
                window.alert(f.concat(": success callback was not provided, or is not a function"))
            }
        }
        else {
            const error = new Error(f.concat(": Failed to mark form ").concat(formId).concat(` as uploaded. Status: ${response.statusText}`))
            error.code = response.status
            throw error
        }
    })
}

export const CreateDeviceForm = async function (businessEntityId, deviceType, name, description, diskFileName, file, successCallback, errorCallback, config, accessToken) {
    const f = "CreateDeviceForm"
    const isZipFile = file.name.includes(".zip")
    try {
        const url = `${config.managementServiceBaseUrl}/businessentities/${businessEntityId}/componentDeviceForms/create?iszipfile=${isZipFile}`
        fetch(url, {
            method: "POST",
            cache: "no-cache",
            mode: "cors",
            referrerPolicy: "origin",
            headers: {
                "Content-Type": "application/json",
                'Authorization': `Token token="${accessToken}"`,
                'Shift4-Rest-Request-Envelope-Version': '1',
                'Shift4-Rest-Response-Envelope-Version': '1',
                'Clientid': '4'
            },
            body: JSON.stringify({
                deviceType: deviceType,
                name: name,
                description: description,
                businessEntityId: businessEntityId
            })
        }).then(async (response) => {
            console.log(f.concat(": got a response from the server"))
            if (response.ok) {
                console.log(f.concat(": response is OK"))
                response.json().then(async (parsed) => {
                    console.log(f.concat(": About to log response"))
                    console.log(parsed)
                    console.log(f.concat(": Logged response"))
                    const presignedUrl = parsed.presignedURL.url
                    const formId = parsed.presignedURL.id
                    const uploadSuccessCallback = async function () {
                        console.log(f.concat(": inside the post-file upload success callback for marking form as uploaded"))
                        const updateSuccessCallback = function () {
                            console.log(f.concat(": inside the update success callback"))
                            if (typeof successCallback == 'function') {
                                console.log(f.concat(": about to execute success callback passed to the outer function"))
                                successCallback()
                            }
                            else {
                                console.log(f.concat(": success callback is not a function"))
                            }
                        }
                        UpdateDeviceFormUploadStatus(businessEntityId, formId, isZipFile, updateSuccessCallback, config, accessToken)
                    }
                    const uploadErrorCallback = function () {
                        console.error(f.concat(": file upload failed"))
                        UpdateDeviceFormUploadStatus(businessEntityId, formId, isZipFile, null, config, accessToken)
                    }
                    uploadFileToPresignedURL(file, presignedUrl, uploadSuccessCallback, uploadErrorCallback)
                })
            }
            else {
                const error = new Error(f.concat(`: Failed to create component device form. Status: ${response.statusText}`))
                error.code = response.status
                throw error
            }
        });
    } catch (x) {
        const err = "Caught exception ".concat(x.toString())
        console.error(err)
        console.trace()
        window.alert(err)
        if (typeof errorCallback == 'function') {
            console.log(f.concat(": about to execute error callback"))
            errorCallback()
        }
        else {
            console.log(f.concat(": error callback is not a function"))
        }
    }
}

export const DownloadDeviceForm = async function (businessEntityId, record, config, accessToken, success_callback) {
    let url = `${config.managementServiceBaseUrl}/businessentities/${businessEntityId}/componentDeviceForms/${record.id}/forms.tgz`
    let filename = record.name
    fetch(url, {
        method: 'GET',
        cache: "no-cache",
        mode: "cors",
        referrerPolicy: "origin",
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
            Authorization: `Token token="${accessToken}"`,
        }
    }).then(async (response) => {
        let content = new Uint8Array()
        for await (const chunk of response.body) {
            let temp = new Uint8Array(content.length + chunk.length)
            temp.set(content)
            temp.set(chunk, content.length)
            content = temp
        }
        let saveFileNameAs
        if (filename.indexOf('.tgz') == -1) {
            saveFileNameAs = filename.concat('.tgz')
        }
        else {
            saveFileNameAs = filename;
        }
        saveFile(content, saveFileNameAs, 'application/x-gzip');
        success_callback()
    })
}

export const DeleteDeviceForm = async function (businessEntityId, record, config, accessToken, success_callback, error_callback) {
    try {
        let url = `${config.managementServiceBaseUrl}/businessentities/${businessEntityId}/componentDeviceForms/${record.id}`
        const response = await fetch(url, {
            method: "POST",
            cache: "no-cache",
            mode: "cors",
            referrerPolicy: "origin",
            headers: {
                'X-HTTP-Method-Override': 'DELETE',
                'Authorization': `Token token="${accessToken}"`,
                'Shift4-Rest-Request-Envelope-Version': '1',
                'Shift4-Rest-Response-Envelope-Version': '1',
                'Clientid': '4'
            }
        })
        if (!response.ok) {
            let error
            if (response.status == 401) {
                // unauthorized
                error = new Error('Unauthorized: Please login from the old environment.')
                error.code = "401"
            }
            else {
                error = new Error(`Failed to delete component device form. Status: ${response.statusText}`)
                error.code = response.status
            }
            throw error
        }
        success_callback()
        return response;
    } catch (error) {
        error_callback(error)
    }
}

/****************************************************************************
 * 
 * UTG / Component
 * 
 ***************************************************************************/

/**
 * Fetch UTG List for a given Business Entity ID
 * @param {string} businessEntityId - The Business Entity ID
 * @param {object} filter - The filter object containing the predicates array
 * @param {object} config - The application config object
 * @param {object} accessToken - The logged in user's accessToken
 * @returns {Promise} - The UTG list as a Promise
 */
export const fetchUtgList = async (businessEntityId, filters, config, accessToken) => {
    const url = `${config.managementServiceBaseUrl}/businessentities/${businessEntityId}/components/query`
    const componentsFilter = filters ? JSON.stringify(filters) : '{"Predicates": []}';
    const response = await fetch(url, {
        method: "POST",
        cache: "no-cache",
        headers: {
            "Content-Type": "application/json",
            'Authorization': `Token token="${accessToken}"`
        },
        body: JSON.stringify(componentsFilter).replace("\n", "") // make sure we encode the query correctly
    })

    if (!response.ok) {
        let error
        if (response.status == 401) {
            // unauthorized
            error = new Error('Unauthorized: Please login from the old environment.')
            error.code = "401"
        }
        else {
            error = new Error(`Failed to fetch UTG List for Business Entity ID: ${businessEntityId}. Status: ${response.statusText}`)
            error.code = response.status
        }
        throw error
    }
    return await response.json();
};



/**
 * Fetch Component for a given Business Entity ID and Component ID. This returns a component record rather than the view item.
 * @param {string} businessEntityId - The Business Entity ID
 * @param {string} componentId - The Component ID
 * @param {object} config - The application config object
 * @param {object} accessToken - The logged in user's accessToken
 * @returns {Promise} - The Component as a Promise
 */
export const fetchComponent = async (businessEntityId, componentId, config, accessToken) => {
    const url = `${config.managementServiceBaseUrl}/businessentities/${businessEntityId}/components/${componentId}`
    const response = await fetch(url, {
        method: "GET",
        cache: "no-cache",
        mode: "cors",
        referrerPolicy: "origin",
        headers: {
            "Content-Type": "application/json",
            'Authorization': `Token token="${accessToken}"`
        }
    })
    if (!response.ok) {
        let error
        if (response.status == 401) {
            // unauthorized
            error = new Error('Unauthorized: Please login from the old environment.')
            error.code = "401"
        }
        else {
            error = new Error(`Failed to fetch Component for Business Entity ID: ${businessEntityId}. Status: ${response.statusText}`)
            error.code = response.status
        }
        throw error
    }
    return (await response.json());
};

/**
 * Fetch UTG for a given Business Entity ID and Component ID
 * @param {string} businessEntityId - The Business Entity ID
 * @param {string} componentId - The Component ID
 * @param {object} config - The application config object
 * @param {object} accessToken - The logged in user's accessToken
 * @returns {Promise} - The UTG as a Promise
 */
export const fetchUtg = async (businessEntityId, componentId, config, accessToken) => {
    const url = `${config.managementServiceBaseUrl}/businessentities/${businessEntityId}/components/query`
    const componentsFilter = `{"Predicates": [{"PropertyName":"ComponentId","Operator":"EQ","Value":"${componentId}","TypeHint": "Integer"}]}`
    const response = await fetch(url, {
        method: "POST",
        cache: "no-cache",
        mode: "cors",
        referrerPolicy: "origin",
        headers: {
            "Content-Type": "application/json",
            'Authorization': `Token token="${accessToken}"`
        },
        body: JSON.stringify(componentsFilter).replace("\n", "") // make sure we encode the query correctly
    })
    if (!response.ok) {
        let error
        if (response.status == 401) {
            // unauthorized
            error = new Error('Unauthorized: Please login from the old environment.')
            error.code = "401"
        }
        else {
            error = new Error(`Failed to fetch UTG for Business Entity ID: ${businessEntityId}. Status: ${response.statusText}`)
            error.code = response.status
        }
        throw error
    }
    return (await response.json())[0];
};

/**
 * Export UTG List
 * @param {string} businessEntityId - The Business Entity ID
 * @param {object} config - The application config object
 * @param {object} accessToken - The logged in user's accessToken
 * @returns {Promise} - The update result as a Promise
 */
export const ExportUtgList = async (businessEntityId, config, accessToken) => {
    const url = `${config.managementServiceBaseUrl}/businessentities/${businessEntityId}/components/query/csv`
    const tasksFilter = '{"Predicates": []}'
    // IMPORTANT: The Shift4 envelope headers are required by the REST api to properly query the data. Also note that this is PUT rather than POST.
    const response = await fetch(url, {
        method: "POST",
        cache: "no-cache",
        mode: "cors",
        referrerPolicy: "origin",
        headers: {
            "Content-Type": "application/json",
            'Authorization': `Token token="${accessToken}"`,
            'Shift4-Rest-Request-Envelope-Version': '1',
            'Shift4-Rest-Response-Envelope-Version': '1',
            'Clientid': '4'
        },
        body: JSON.stringify(tasksFilter).replace("\n", "") // make sure we encode the query correctly
    })

    if (!response.ok) {
        let error
        if (response.status == 401) {
            // unauthorized
            error = new Error('Unauthorized: Please login from the old environment.')
            error.code = "401"
        }
        else {
            error = new Error(`Failed to export tasks. Status: ${response.statusText}`)
            error.code = response.status
        }
        throw error
    }
    return await response.arrayBuffer()
}


/**
 * Update Component
 * @param {string} businessEntityId - The Business Entity ID
 * @param {string} component - The utg/component object. This object contains businessEntityId, machinePermanentId, and componentId
 * @param {object} config - The application config object
 * @param {object} accessToken - The logged in user's accessToken
 * @returns {Promise} - The update result as a Promise
 */
export const UpdateComponent = async (businessEntityId, component, config, accessToken) => {
    const url = `${config.managementServiceBaseUrl}/businessentities/${businessEntityId}/machines/${component.Machine.PermanentId}/components/${component.Id}`

    // IMPORTANT: The Shift4 envelope headers are required by the REST api to properly query the data. The method is POST but header tells server to change this to PUT to 
    // match the previous behavior in emberjs
    const response = await fetch(url, {
        method: "PUT",
        cache: "no-cache",
        mode: "cors",
        referrerPolicy: "origin",
        headers: {
            "Content-Type": "application/json",
            'Authorization': `Token token="${accessToken}"`,
            'Shift4-Rest-Request-Envelope-Version': '1',
            'Shift4-Rest-Response-Envelope-Version': '1'
        },
        body: JSON.stringify(component)
    })

    if (!response.ok) {
        let error
        if (response.status == 401) {
            // unauthorized
            error = new Error('Unauthorized: Please login from the old environment.')
            error.code = "401"
        }
        else {
            error = new Error(`Failed to update component. Status: ${response.statusText}`)
            error.code = response.status
        }
        throw error
    }
    return await response.json();
};


/**
 * Fetch Component Config for a given Component
 * @param {string} businessEntityId - The Business Entity ID
 * @param {string} machinePermanentId - The Machine Permanent ID
 * @param {string} componentPermanentId - The Component Permanent ID
 * @param {object} config - The application config object
 * @param {object} accessToken - The logged in user's accessToken
 * @returns {Promise} - The Component Config as a Promise
 */
export const fetchComponentConfig = async (businessEntityId, machinePermanentId, componentPermanentId, config, accessToken) => {
    if (`${machinePermanentId}` == "undefined") {
        throw new Error("Machine permanent ID is undefined")
    }
    else if (`${componentPermanentId}` == 'undefined') {
        throw new Error("Component permanent ID is undefined")
    }
    const url = `${config.managementServiceBaseUrl}/businessentities/${businessEntityId}/machines/${machinePermanentId}/components/${componentPermanentId}/componentConfigs/0`
    const response = await fetch(url, {
        method: "GET",
        headers: {
            "Content-Type": "application/json",
            'Authorization': `Token token="${accessToken}"`,
            'Shift4-Rest-Request-Envelope-Version': '1',
            'Shift4-Rest-Response-Envelope-Version': '1'
        }
    })
    if (!response.ok) {
        let error
        if (response.status == 401) {
            // unauthorized
            error = new Error('Unauthorized: Please login from the old environment.')
            error.code = "401"
        }
        else {
            error = new Error(`Failed to fetch Component Config: ${businessEntityId}. Status: ${response.statusText}`)
            error.code = response.status
        }
        throw error
    }
    return await response.json();
};

// https://in4mapi.cctest-s4.com/in4m/api/businessEntities/8647/machines/dcf16126-d4ad-40ce-8598-a790d283596a/components/56ff4dd2-5676-4ef4-af6c-f2ced640b303/componentConfigs/0

/**
 * Update Component Config
 * @param {string} businessEntityId - The Business Entity ID
 * @param {string} machinePermanentId - The Machine Permanent ID
 * @param {string} componentPermanentId - The Component Permanent ID
 * @param {object} componentConfig - The updated component config to send to the database
 * @param {object} config - The application config object
 * @param {object} accessToken - The logged in user's accessToken
 * @returns {Promise} - The update result as a Promise
 */
export const UpdateComponentConfig = async (businessEntityId, machinePermanentId, componentPermanentId, componentConfig, config, accessToken) => {
    const url = `${config.managementServiceBaseUrl}/businessEntities/${businessEntityId}/machines/${machinePermanentId}/components/${componentPermanentId}/componentConfigs/0`

    // IMPORTANT: The Shift4 envelope headers are required by the REST api to properly query the data. Also note that this is PUT rather than POST.
    const response = await fetch(url, {
        method: "PUT",
        cache: "no-cache",
        mode: "cors",
        referrerPolicy: "origin",
        headers: {
            "Content-Type": "application/json",
            'Authorization': `Token token="${accessToken}"`,
            'Shift4-Rest-Request-Envelope-Version': '1',
            'Shift4-Rest-Response-Envelope-Version': '1',
            'Clientid': '4'
        },
        body: JSON.stringify(componentConfig)
    })

    if (!response.ok) {
        let error
        if (response.status == 401) {
            // unauthorized
            error = new Error('Unauthorized: Please login from the old environment.')
            error.code = "401"
        }
        else {
            error = new Error(`Failed to update componentConfig. Status: ${response.statusText}`)
            error.code = response.status
        }
        throw error
    }
    return await response.json();
};



/**
 * Remove UTG from command center
 * @param {string} businessEntityId - The Business Entity ID
 * @param {string} machinePermanentId - The globally unique machine permanent Id
 * @param {object} config - The application config object
 * @param {object} accessToken - The logged in user's accessToken
 * @returns {Promise} - The result as a Promise
 */
export const DeleteUtg = async (businessEntityId, machinePermanentId, componentPermanentId, config, accessToken) => {
    const url = `${config.managementServiceBaseUrl}/businessentities/${businessEntityId}/machines/${machinePermanentId}`
    // NOTE that this actually inactivates the machine record that the UTG is connected to

    // IMPORTANT: The Shift4 envelope headers are required by the REST api. Also note the server requires POST with a header override to indicate DELETE
    const response = await fetch(url, {
        method: "POST",
        cache: "no-cache",
        mode: "cors",
        referrerPolicy: "origin",
        headers: {
            'X-HTTP-Method-Override': 'DELETE',
            'Authorization': `Token token="${accessToken}"`,
            'Shift4-Rest-Request-Envelope-Version': '1',
            'Shift4-Rest-Response-Envelope-Version': '1',
            'Clientid': '4'
        }
    })

    if (!response.ok) {
        let error
        if (response.status == 401) {
            // unauthorized
            error = new Error('Unauthorized: Please login from the old environment.')
            error.code = "401"
        }
        else {
            error = new Error(`Failed to remove the UTG. Status: ${response.statusText}`)
            error.code = response.status
        }
        throw error
    }
    return response;
};


/****************************************************************************
 * 
 * Schemas
 * 
 ***************************************************************************/

/**
 * Fetch Component Config Schema for a given UTG Version
 * @param {string} utgVersion - The UTG Version
 * @returns {Promise} - The Config Schema as a Promise
 */
export const fetchComponentConfigSchema = async (config, utgVersion) => {
    const url = `${config.managementServiceBaseUrl}/componentconfigschemas?componenttype=1&componentversion=${utgVersion}`
    const response = await fetch(url);
    if (!response.ok) {
        let error
        if (response.status == 401) {
            // unauthorized
            error = new Error('Unauthorized: Please login from the old environment.')
            error.code = "401"
        }
        else {
            error = new Error(`Failed to fetch Component Config Schema for UTG Version: ${utgVersion}. Status: ${response.statusText}`)
            error.code = response.status
        }
        throw error
    }
    return await response.json();
};

/**
 * Fetch Local Component Config Schema during development
 * @returns {Promise} - The Config Schema as a Promise
 */ 
export const fetchComponentConfigSchemaLocal = async (utgVersion) => {
    const url = `/schemas/configs/${utgVersion}/config-schema.json`
    const response = await fetch(url, {
        method: 'GET',
        headers: {
            Accept: 'application/json'
        }
    });
    if (!response.ok) {
        let error = new Error(`Failed to fetch Local Component Config Schema. Status: ${response.statusText}`)
        error.code = response.status
        throw error
    }
    return await response.json();
};

/**
 * Fetch Device Schemas
 * @param {object} config - The application config object
 * @param {object} accessToken - The logged in user's accessToken
 * @returns {Promise} - The Installation as a Promise
 */
export const fetchDeviceSchemas = async (config, accessToken) => {
    const url = `${config.managementServiceBaseUrl}/componentDevices?version=2`
    const response = await fetch(url, {
        method: "GET",
        headers: {
            "Content-Type": "application/json",
            'Authorization': `Token token="${accessToken}"`,
            'Shift4-Rest-Request-Envelope-Version': '1',
            'Shift4-Rest-Response-Envelope-Version': '1'
        }
    })
    if (!response.ok) {
        let error
        if (response.status == 401) {
            // unauthorized
            error = new Error('Unauthorized: Please login from the old environment.')
            error.code = "401"
        }
        else {
            error = new Error(`Failed to fetch Device Schemas: Status: ${response.statusText}`)
            error.code = response.status
        }
        throw error
    }
    return await response.json().then((item) => { return item.componentDevice })
};

/**
 * Fetch Local Device Schemas during development
 * @returns {Promise} - The Schemas as a Promise
 */
export const fetchDeviceSchemasLocal = async () => {
    const url = `/jsonform/lib/componentdevices.json`
    const response = await fetch(url, {
        method: 'GET',
        headers: {
            Accept: 'application/json'
        }
    });
    if (!response.ok) {
        let error = new Error(`Failed to fetch Local Component Config Schema. Status: ${response.statusText}`)
        error.code = response.status
        throw error
    }
    return await response.json();
};

/****************************************************************************
 * 
 * Machine Status (From DynamoDB)
 * 
 ***************************************************************************/

/**
 * Fetch Machine Status for a given Business Entity ID and Permanent Machine Id
 * @param {string} businessEntityId - The Business Entity ID
 * @param {string} machinePermanentId - The Machine Permanent ID
 * @param {object} config - The application config object
 * @param {object} accessToken - The logged in user's accessToken
 * @returns {Promise} - The Machine Status as a Promise
 */
export const fetchMachineStatus = async (businessEntityId, machinePermanentId, config, accessToken) => {
    // const url = `${config.managementServiceBaseUrl}/businessentities/${businessEntityId}/machineStatus?machinePermanentIds=${machinePermanentId}`
    const url = `${config.managementServiceBaseUrl}/businessentities/${businessEntityId}/machineStatus/${machinePermanentId}`
    const response = await fetch(url, {
        method: "GET",
        headers: {
            "Content-Type": "application/json",
            'Authorization': `Token token="${accessToken}"`
        }
    })
    if (!response.ok) {
        let error
        if (response.status == 401) {
            // unauthorized
            error = new Error('Unauthorized: Please login from the old environment.')
            error.code = "401"
        }
        else {
            error = new Error(`Failed to fetch Machine Status for Business Entity ID: ${businessEntityId}. Status: ${response.statusText}`)
            error.code = response.status
        }
        throw error
    }
    return await response.json();
};



/****************************************************************************
 * 
 * Installations
 * 
 ***************************************************************************/

/**
 * Fetch Installations for a given business entity
 * @param {string} businessEntityId - The Business Entity ID
 * @param {object} config - The application config object
 * @param {object} accessToken - The logged in user's accessToken
 * @returns {Promise} - The Installation as a Promise
 */
export const fetchInstallations = async (businessEntityId, config, accessToken) => {
    const url = `${config.managementServiceBaseUrl}/businessentities/${businessEntityId}/installations`
    const response = await fetch(url, {
        method: "GET",
        headers: {
            "Content-Type": "application/json",
            'Authorization': `Token token="${accessToken}"`,
            'Shift4-Rest-Request-Envelope-Version': '1',
            'Shift4-Rest-Response-Envelope-Version': '1'
        }
    })
    if (!response.ok) {
        let error
        if (response.status == 401) {
            // unauthorized
            error = new Error('Unauthorized: Please login from the old environment.')
            error.code = "401"
        }
        else {
            error = new Error(`Failed to fetch Installations: ${businessEntityId}. Status: ${response.statusText}`)
            error.code = response.status
        }
        throw error
    }
    return await response.json().then((item) => { return item.installation })
};

/**
 * Create Installations for a given business entity
 * @param {string} businessEntityId - The Business Entity ID
 * @param {object} config - The application config object
 * @param {object} installationManifest - The installation manifest
 * @param {object} accessToken - The logged in user's accessToken
 * @returns {Promise} - The Installation as a Promise
 */
export const CreateInstallation = async(businessEntityId, config, installationManifest, accessToken) => {
    const url = `${config.managementServiceBaseUrl}/businessentities/${businessEntityId}/installations`
    const response = await fetch(url, {
        method: "POST",
        referrerPolicy: "no-referrer-when-downgrade",
        headers: {
            "Content-Type": "application/json",
            'Authorization': `Token token="${accessToken}"`,
            'Shift4-Rest-Request-Envelope-Version': '1',
            'Shift4-Rest-Response-Envelope-Version': '1',
            'ClientId': '4'
        },
        body: JSON.stringify(installationManifest)
    })
    if (!response.ok) {
        let error
        if (response.status == 401) {
            // unauthorized
            error = new Error('Unauthorized: Please login from the old environment.')
            error.code = "401"
        }
        else {
            error = new Error(`Failed to create installation for Business Entity ID: ${businessEntityId}. Status: ${response.statusText}`)
            error.code = response.status
        }
        throw error
    }
    return await response.json()
}

/**
 * Downlaod Install Package
 * @param {object} config - The application config object
 * @param {object} verificationId - The Verification Id of the requested download package
 * @returns {Promise} - The update result as a Promise
 */
export const DownloadInstallerPackage = async (config, verificationId) => {
    const url = `${config.managementServiceBaseUrl}/installations/packagezip?verificationId=${verificationId}`

    const response = await fetch(url, {
        method: "GET",
        cache: "no-cache",
        mode: "cors",
        referrerPolicy: "origin",
        headers: {
            "Content-Type": "application/octet"
        }
    })

    if (!response.ok) {
        let error
        error = new Error(`Failed to download the installer package. Status: ${response.status} ${response.statusText}`)
        error.code = response.status
        throw error
    }
    return await response.arrayBuffer();
};

/**
 * Deactivate Installation
 * @param {object} config - The application config object
 * @param {string} beId - The business entity id of the installation to deactivate
 * @param {string} installationId = The id of the installation to deactivate
 * @param {object} accessToken - The logged in user's accessToken
 * @returns {Promise} - The update result as a Promise
 */
export const DeactivateInstallation = async (config, beId, installationId, accessToken) => {
    const url = `${config.managementServiceBaseUrl}/businessentities/${beId}/installations/${installationId}`

    // IMPORTANT: The Shift4 envelope headers are required by the REST api to properly query the data. Also note that this is PUT rather than POST.
    const response = await fetch(url, {
        method: "DELETE",
        cache: "no-cache",
        mode: "cors",
        referrerPolicy: "origin",
        headers: {
            "Content-Type": "application/json",
            'Authorization': `Token token="${accessToken}"`,
            'Shift4-Rest-Request-Envelope-Version': '1',
            'Shift4-Rest-Response-Envelope-Version': '1',
            'Clientid': '4'
        }
    })

    if (!response.ok) {
        let error
        error = new Error(`Failed to deactivate install. Status: ${response.status} ${response.statusText}`)
        error.code = response.status
        throw error
    }
    return response;
};

/****************************************************************************
 * 
 * Business Entities
 * 
 ***************************************************************************/

/**
 * Fetch Business Entities
 * @param {string} rootBusinessEntityId - The Business Entity ID in the hierarchy to start the query from
 * @param {bool} includeChildren - Includes nested business entities. Default is true
 * @param {bool} includeParent - Returns the rootBusinessEntityId in the query. Default is true
 * @param {object} config - The application config object
 * @param {object} accessToken - The logged in user's accessToken
 * @returns {Promise} - The Installation as a Promise
 */
export const fetchBusinessEntities = async (rootBusinessEntityId, includeChildren = true, includeParent = true, config, accessToken) => {
    let queryParameters = []
    if (includeChildren) {
        queryParameters.push(['includeChildren', includeChildren])
    }
    if (includeParent) {
        queryParameters.push(['includeParent', includeParent])
    }
    if (rootBusinessEntityId) {
        queryParameters.push(['parentBusinessEntityId', rootBusinessEntityId])
    }
    // buildUrlWithQueryParameters takes an array of parameters. Each parameter is a 2 dimensional array. index 0 == key, index 1 == value
    // note that this calls the security service instead of the management api
    const url = buildUrlWithQueryParameters(`${config.securityServiceBaseUrl}/businessentitysummaries`, queryParameters)
    const response = await fetch(url, {
        method: "GET",
        headers: {
            "Content-Type": "application/json",
            'Authorization': `Token token="${accessToken}"`,
            'Clientid': 4,
            'Shift4-Rest-Request-Envelope-Version': '1',
            'Shift4-Rest-Response-Envelope-Version': '1'
        }
    })
    if (!response.ok) {
        let error
        if (response.status == 401) {
            // unauthorized
            error = new Error('Unauthorized: Please login from the old environment.')
            error.code = "401"
        }
        else {
            error = new Error(`Failed to fetch Business Entities under: Status: ${response.statusText}`)
            error.code = response.status
        }
        throw error
    }
    return await response.json().then(result => result.businessEntitySummary)
};





/****************************************************************************
 * 
 * Profiles
 * 
 ***************************************************************************/


/**
 * Fetch Component Profile Schema for a given UTG Version
 * @param {string} utgVersion - The UTG Version
 * @returns {Promise} - The Config Schema as a Promise
 */
export const fetchComponentProfileSchema = async (utgVersion) => {
    const url = `https://in4mapi.cctest-s4.com/in4m/api/profileschemas?componentversion=${utgVersion}`
    const response = await fetch(url);
    if (!response.ok) {
        let error
        if (response.status == 401) {
            // unauthorized
            error = new Error('Unauthorized: Please login from the old environment.')
            error.code = "401"
        }
        else {
            error = new Error(`Failed to fetch Component Config Schema for UTG Version: ${utgVersion}. Status: ${response.statusText}`)
            error.code = response.status
        }
        throw error
    }
    return await response.json();
};


/**
 * Create New Profile
 * @param {string} businessEntityId - The Business Entity ID
 * @param {string} profileId - The Profile ID
 * @param {object} profileConfig - The updated profile config to send to the database
 * @param {object} config - The application config object
 * @param {object} accessToken - The logged in user's accessToken
 * @returns {Promise} - The update result as a Promise
 */
export const CreateProfile = async (businessEntityId, profileConfig, config, accessToken) => {
    const url = `${config.managementServiceBaseUrl}/businessentities/${businessEntityId}/componentProfiles`

    // IMPORTANT: The Shift4 envelope headers are required by the REST api to properly query the data. Also note that this is PUT rather than POST.
    const response = await fetch(url, {
        method: "POST",
        cache: "no-cache",
        mode: "cors",
        referrerPolicy: "origin",
        headers: {
            "Content-Type": "application/json",
            'Authorization': `Token token="${accessToken}"`,
            'Shift4-Rest-Request-Envelope-Version': '1',
            'Shift4-Rest-Response-Envelope-Version': '1',
            'Clientid': '4'
        },
        body: JSON.stringify(profileConfig)
    })

    if (!response.ok) {
        let error
        if (response.status == 401) {
            // unauthorized
            error = new Error('Unauthorized: Please login from the old environment.')
            error.code = "401"
        }
        else {
            error = new Error(`Failed to update profile. Status: ${response.statusText}`)
            error.code = response.status
        }
        throw error
    }
    return await response.json();
};

/**
 * Fetch Component Profiles for a given Business Entity ID
 * @param {string} businessEntityId - The Business Entity ID
 * @param {object} config - The application config object
 * @param {object} accessToken - The logged in user's accessToken
 * @returns {Promise} - The Component Profiles as a Promise
 */
export const fetchComponentProfiles = async (businessEntityId, config, accessToken) => {
    const url = `${config.managementServiceBaseUrl}/businessentities/${businessEntityId}/componentProfiles`
    const response = await fetch(url, {
        method: "GET",
        headers: {
            "Content-Type": "application/json",
            'Authorization': `Token token="${accessToken}"`,
            'Shift4-Rest-Request-Envelope-Version': '1',
            'Shift4-Rest-Response-Envelope-Version': '1'
        }
    })
    if (!response.ok) {
        let error
        if (response.status == 401) {
            // unauthorized
            error = new Error('Unauthorized: Please login from the old environment.')
            error.code = "401"
        }
        else {
            error = new Error(`Failed to fetch Component Profiles for Business Entity ID: ${businessEntityId}. Status: ${response.statusText}`)
            error.code = response.status
        }
        throw error
    }
    return await response.json();
};

/**
 * Fetch Component Profiles for a given Business Entity ID and profile ID
 * @param {string} businessEntityId - The Business Entity ID
 * @param {string} profileId - The Profile ID
 * @param {object} config - The application config object
 * @param {object} accessToken - The logged in user's accessToken
 * @returns {Promise} - The Component Profile as a Promise
 * api/businessentities/{businessEntityId}/componentProfiles/{profileId}
 */
export const fetchComponentProfile = async (businessEntityId, profileId, config, accessToken) => {
    const url = `${config.managementServiceBaseUrl}/businessentities/${businessEntityId}/componentProfiles/${profileId}`
    const response = await fetch(url, {
        method: "GET",
        headers: {
            "Content-Type": "application/json",
            'Authorization': `Token token="${accessToken}"`,
            'Shift4-Rest-Request-Envelope-Version': '1',
            'Shift4-Rest-Response-Envelope-Version': '1'
        }
    })
    if (!response.ok) {
        let error
        if (response.status == 401) {
            // unauthorized
            error = new Error('Unauthorized: Please login from the old environment.')
            error.code = "401"
        }
        else {
            error = new Error(`Failed to fetch Component Profiles for Business Entity ID: ${businessEntityId}. Status: ${response.statusText}`)
            error.code = response.status
        }
        throw error
    }
    return await response.json();
};


/**
 * Update Profile Config
 * @param {string} businessEntityId - The Business Entity ID
 * @param {string} profileId - The Profile ID
 * @param {object} profileConfig - The updated profile config to send to the database
 * @param {object} config - The application config object
 * @param {object} accessToken - The logged in user's accessToken
 * @returns {Promise} - The update result as a Promise
 */
export const UpdateProfile = async (businessEntityId, profileId, profileConfig, config, accessToken) => {
    const url = `${config.managementServiceBaseUrl}/businessentities/${businessEntityId}/componentProfiles/${profileId}`

    // IMPORTANT: The Shift4 envelope headers are required by the REST api to properly query the data. Also note that this is PUT rather than POST.
    const response = await fetch(url, {
        method: "PUT",
        cache: "no-cache",
        mode: "cors",
        referrerPolicy: "origin",
        headers: {
            "Content-Type": "application/json",
            'Authorization': `Token token="${accessToken}"`,
            'Shift4-Rest-Request-Envelope-Version': '1',
            'Shift4-Rest-Response-Envelope-Version': '1',
            'Clientid': '4'
        },
        body: JSON.stringify(profileConfig)
    })

    if (!response.ok) {
        let error
        if (response.status == 401) {
            // unauthorized
            error = new Error('Unauthorized: Please login from the old environment.')
            error.code = "401"
        }
        else {
            error = new Error(`Failed to update profile. Status: ${response.statusText}`)
            error.code = response.status
        }
        throw error
    }
    return await response.json();
};

/**
 * Delete Profile
 * @param {string} businessEntityId - The Business Entity ID
 * @param {string} profileId - The Profile ID
 * @param {string} name - The Profile Name
 * @param {object} config - The application config object
 * @param {object} accessToken - The logged in user's accessToken
 * @returns {Promise} - The update result as a Promise
 */
export const DeleteProfile = async (businessEntityId, profileId, name, config, accessToken) => {
    name = encodeURIComponent(name)
    const url = `${config.managementServiceBaseUrl}/businessentities/${businessEntityId}/componentprofiles/${profileId}?profilename=${name}`

    // IMPORTANT: The Shift4 envelope headers are required by the REST api to properly query the data. Also note that this is PUT rather than POST.
    const response = await fetch(url, {
        method: "DELETE",
        cache: "no-cache",
        mode: "cors",
        referrerPolicy: "origin",
        headers: {
            "Content-Type": "application/json",
            'Authorization': `Token token="${accessToken}"`,
            'Shift4-Rest-Request-Envelope-Version': '1',
            'Shift4-Rest-Response-Envelope-Version': '1',
            'Clientid': '4'
        }
    })

    if (!response.ok) {
        let error
        if (response.status == 401) {
            // unauthorized
            error = new Error('Unauthorized: Please login from the old environment.')
            error.code = "401"
        }
        else {
            error = new Error(`Failed to delete profile. Status: ${response.statusText}`)
            error.code = response.status
        }
        throw error
    }
    return response;
};


/**
 * Apply Profile
 * @param {string} businessEntityId - The Business Entity ID
 * @param {string} profileId - The Profile ID
 * @param {array} componentIds - An array of Component IDs
 * @param {object} config - The application config object
 * @param {object} accessToken - The logged in user's accessToken
 * @returns {Promise} - The update result as a Promise
 */
export const ApplyProfile = async (businessEntityId, profileId, componentIds, config, accessToken) => {
    const url = `${config.managementServiceBaseUrl}/componentProfileAssignments?installationtype=30`

    // The body is a JSON string containing an array of component profile assignment parameters. It is sent as POST with override of PATCH
    // installationtype=30 indicates that these are existing installations

    let data = []
    componentIds.forEach(id => {
        data.push({
            componentId: id,
            componentBusinessEntityId: businessEntityId,
            profileId: profileId,
            profileBusinessEntityId: businessEntityId
        })
    })

    const response = await fetch(url, {
        method: "POST",
        cache: "no-cache",
        mode: "cors",
        referrerPolicy: "origin",
        headers: {
            'X-HTTP-Method-Override': 'PATCH',
            "Content-Type": "application/json",
            'Authorization': `Token token="${accessToken}"`,
            'Shift4-Rest-Request-Envelope-Version': '1',
            'Shift4-Rest-Response-Envelope-Version': '1',
            'Clientid': '4'
        },
        body: JSON.stringify(data)
    })

    if (!response.ok) {
        let error
        if (response.status == 401) {
            // unauthorized
            error = new Error('Unauthorized: Please login from the old environment.')
            error.code = "401"
        }
        else {
            error = new Error(`Failed to apply the profiles. Status: ${response.statusText}`)
            error.code = response.status
        }
        throw error
    }
    return response;
};

/****************************************************************************
 * 
 * Manifests
 * 
 ***************************************************************************/

/**
 * Fetch Agent Manifest
 * @returns {Promise} - The Agent Manifest as a Promise
 */
export const fetchAgentManifest = async (config) => {
    const url = `${config.agentManifestUrl}?uniqifier=${Date.now()}`
    const response = await fetch(url)
    if (!response.ok) {
        let error = new Error(`Failed to fetch Agent Manifest. Status: ${response.statusText}`)
        error.code = response.status
        throw error
    }
    return await response.json();
};

/**
 * Fetch UTG Manifest
 * @returns {Promise} - The UTG Manifest as a Promise
 */
export const fetchUTGManifest = async (config) => {
    const url = `${config.utgManifestUrl}?uniqifier=${Date.now()}`
    const response = await fetch(url)
    if (!response.ok) {
        let error = new Error(`Failed to fetch UTG Manifest. Status: ${response.statusText}`)
        error.code = response.status
        throw error
    }
    return await response.json();
};


/****************************************************************************
 * 
 * Config Templates
 * 
 ***************************************************************************/

export const fetchConfigTemplates = async(businessEntityId, config, accessToken) => {
    const url = `${config.managementServiceBaseUrl}/businessentities/${businessEntityId}/configtemplates`
    const response = await fetch(url, {
        method: "GET",
        headers: {
            "Content-Type": "application/json",
            'Authorization': `Token token="${accessToken}"`,
            'Shift4-Rest-Request-Envelope-Version': '1',
            'Shift4-Rest-Response-Envelope-Version': '1'
        }
    })
    if (!response.ok) {
        let error
        if (response.status == 401) {
            // unauthorized
            error = new Error('Unauthorized: Please login from the old environment.')
            error.code = "401"
        }
        else {
            error = new Error(`Failed to fetch config templates for Business Entity ID: ${businessEntityId}. Status: ${response.statusText}`)
            error.code = response.status
        }
        throw error
    }
    return await response.json().then(c => c.configTemplate)

}

export const fetchConfigTemplate = async(businessEntityId, configTemplateId, config, accessToken) => {
    const url = `${config.managementServiceBaseUrl}/businessentities/${businessEntityId}/configtemplates/${configTemplateId}`
    const response = await fetch(url, {
        method: "GET",
        headers: {
            "Content-Type": "application/json",
            'Authorization': `Token token="${accessToken}"`,
            'Shift4-Rest-Request-Envelope-Version': '1',
            'Shift4-Rest-Response-Envelope-Version': '1'
        }
    })
    if (!response.ok) {
        let error
        if (response.status == 401) {
            // unauthorized
            error = new Error('Unauthorized: Please login from the old environment.')
            error.code = "401"
        }
        else {
            error = new Error(`Failed to fetch Component Profile for Business Entity ID: ${businessEntityId}. Status: ${response.statusText}`)
            error.code = response.status
        }
        throw error
    }
    return await response.json().then(c => c.configTemplate)

}

export const CreateConfigTemplate = async(businessEntityId, configTemplate, config, accessToken) => {
    const url = `${config.managementServiceBaseUrl}/businessentities/${businessEntityId}/configtemplates`
    const response = await fetch(url, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            'Authorization': `Token token="${accessToken}"`,
            'Shift4-Rest-Request-Envelope-Version': '1',
            'Shift4-Rest-Response-Envelope-Version': '1'
        },
        body: JSON.stringify(configTemplate)
    })

    if (!response.ok) {
        let error
        if (response.status == 401) {
            // unauthorized
            error = new Error('Unauthorized: Please login from the old environment.')
            error.code = "401"
        }
        else {
            error = new Error(`Failed to update config template. Status: ${response.statusText}`)
            error.code = response.status
        }
        throw error
    }
    return await response.json().then(c => c.configTemplate)

}

export const UpdateConfigTemplate = async(businessEntityId, configTemplate, config, accessToken) => {
    const url = `${config.managementServiceBaseUrl}/businessentities/${businessEntityId}/configtemplates/${configTemplate.id}`
    const response = await fetch(url, {
        method: "PUT",
        headers: {
            "Content-Type": "application/json",
            'Authorization': `Token token="${accessToken}"`,
            'Shift4-Rest-Request-Envelope-Version': '1',
            'Shift4-Rest-Response-Envelope-Version': '1'
        },
        body: JSON.stringify(configTemplate)
    })

    if (!response.ok) {
        let error
        if (response.status == 401) {
            // unauthorized
            error = new Error('Unauthorized: Please login from the old environment.')
            error.code = "401"
        }
        else {
            error = new Error(`Failed to update config template. Status: ${response.statusText}`)
            error.code = response.status
        }
        throw error
    }
    return await response.json().then(c => c.configTemplate)

}

export const DeleteConfigTemplate = async(businessEntityId, configTemplateId, config, accessToken) => {
    const url = `${config.managementServiceBaseUrl}/businessentities/${businessEntityId}/configtemplates/${configTemplateId}`
    const response = await fetch(url, {
        method: "POST",
        cache: "no-cache",
        mode: "cors",
        referrerPolicy: "origin",
        headers: {
            'X-HTTP-Method-Override': 'DELETE',
            "Content-Type": "application/json",
            'Authorization': `Token token="${accessToken}"`,
            'Shift4-Rest-Request-Envelope-Version': '1',
            'Shift4-Rest-Response-Envelope-Version': '1'
        }
    })

    if (!response.ok) {
        let error
        if (response.status == 401) {
            // unauthorized
            error = new Error('Unauthorized: Please login from the old environment.')
            error.code = "401"
        }
        else {
            error = new Error(`Failed to delete config template. Status: ${response.statusText}`)
            error.code = response.status
        }
        throw error
    }
    return response;

}

export const ApplyConfigTemplate = async(businessEntityId, configTemplateId, componentId, config, accessToken) => {
    const url = `${config.managementServiceBaseUrl}/businessentities/${businessEntityId}/configtemplates/${configTemplateId}/merge/${componentId}`
    const response = await fetch(url, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            'Authorization': `Token token="${accessToken}"`,
            'Shift4-Rest-Request-Envelope-Version': '1',
            'Shift4-Rest-Response-Envelope-Version': '1'
        }
    })

    if (!response.ok) {
        let error
        if (response.status == 401) {
            // unauthorized
            error = new Error('Unauthorized: Please login from the old environment.')
            error.code = "401"
        }
        else {
            error = new Error(`Failed to update config template. Status: ${response.statusText}`)
            error.code = response.status
        }
        throw error
    }
    return response

}

/****************************************************************************
 * 
 * Users
 * 
 ***************************************************************************/

export const fetchUsers = async(businessEntityId, config, accessToken) => {
    const url = `${config.securityServiceBaseUrl}/businessentities/${businessEntityId}/users?fullDetail=false`
    const response = await fetch(url, {
        method: "GET",
        //referrerPolicy: "no-referrer-when-downgrade",
        headers: {
            //"Accept": 'application/json, text/javascript, */*; q=0.01',
            "Content-Type": "application/json",
            'Authorization': `Token token="${accessToken}"`,
            'Shift4-Rest-Request-Envelope-Version': '1',
            'Shift4-Rest-Response-Envelope-Version': '1'
        }
    })
    if (!response.ok) {
        let error
        if (response.status == 401) {
            // unauthorized
            error = new Error('Unauthorized: Please login from the old environment.')
            error.code = "401"
        }
        else {
            error = new Error(`Failed to fetch users for Business Entity ID: ${businessEntityId}. Status: ${response.statusText}`)
            error.code = response.status
        }
        throw error
    }
    return await response.json().then(c => c.user)
}

export const addUsers = async (businessEntityId, user, config, accessToken) => {
    const url = `${config.securityServiceBaseUrl}/businessentities/${businessEntityId}/users`
    console.log(user)
    const response = await fetch(url, {
        url: url,
        method: 'POST',
        referrerPolicy: "no-referrer-when-downgrade",
        body: JSON.stringify(user),
        headers: {
            //"Accept": 'application/json, text/javascript, */*; q=0.01',
            "Accept": 'application/json',
            "Content-Type": "application/json",
            'Authorization': `Token token="${accessToken}"`,
            "ClientId": 4,
            'Shift4-Rest-Request-Envelope-Version': '1',
            'Shift4-Rest-Response-Envelope-Version': '1',
            //"X-HTTP-Method-Override": "PUT"
        }
    })
    if (!response.ok) {
        let error
        if (response.status == 401) {
            // unauthorized
            error = new Error('Unauthorized: Please login from the old environment.')
            error.code = "401"
        }
        else {
            error = new Error(`Failed to insert user for Business Entity ID: ${businessEntityId}. Status: ${response.statusText}`)
            error.code = response.status
        }
        throw error
    }
    console.log("Successfully inserted new user")
    const parsed = await response.json()
    return parsed.user
}

export const addRole = async function (businessEntityId, userId, roleId, requestBody, config, accessToken) {
    const url = `${config.securityServiceBaseUrl}/users/${userId}/roles/${roleId}/businessentities`
    console.log("Now attempting to add user role")
    console.log(requestBody)
    const response = await fetch(url, {
        url: url,
        method: 'POST',
        referrerPolicy: "no-referrer-when-downgrade",
        body:JSON.stringify(requestBody),
        headers: {
            "Accept": 'application/json',
            "Content-Type": "application/json",
            'Authorization': `Token token="${accessToken}"`,
            "ClientId": 4,
            'Shift4-Rest-Request-Envelope-Version': '1',
            'Shift4-Rest-Response-Envelope-Version': '1'
        }
    })
    if (!response.ok) {
        console.error("Failed to insert new user role")
        let error
        if (response.status == 401) {
            // unauthorized
            error = new Error('Unauthorized: Please login from the old environment.')
            error.code = "401"
        }
        else {
            error = new Error(`Failed to insert user for Business Entity ID: ${businessEntityId}. Status: ${response.statusText}`)
            error.code = response.status
        }
        throw error
    }
    console.log("Successfully inserted new user role")
    return await response.json()
}

export const fetchUserByAccessToken = async(config, accessToken) => {
    const url = `${config.securityServiceBaseUrl}/users?accesstoken=${accessToken}`
    const response = await fetch(url, {
        method: "GET",
        referrerPolicy: "no-referrer-when-downgrade",
        headers: {
            "Content-Type": "application/json",
            'Authorization': `Token token="${accessToken}"`,
            'Shift4-Rest-Request-Envelope-Version': '1',
            'Shift4-Rest-Response-Envelope-Version': '1'
        }
    })
    if (!response.ok) {
        let error
        if (response.status == 401) {
            // unauthorized
            error = new Error('Unauthorized: Please login from the old environment.')
            error.code = "401"
        }
        else {
            const message = `Failed to fetch user. Status: ${response.statusText}`
            console.error(message)
            error = new Error(message)
            error.code = response.status
        }
        throw error
    }
    return await response.json().then(c => c.user[0])
}

export const CreateEphemeralUser = async(businessEntityId, config, ephemeralUser, accessToken) => {
    const url = `${config.securityServiceBaseUrl}/businessentities/${businessEntityId}/ephemeralUsers`
    const response = await fetch(url, {
        method: "POST",
        referrerPolicy: "no-referrer-when-downgrade",
        headers: {
            "Content-Type": "application/json",
            'Authorization': `Token token="${accessToken}"`,
            'Shift4-Rest-Request-Envelope-Version': '1',
            'Shift4-Rest-Response-Envelope-Version': '1',
            'ClientId': '4'
        },
        body: JSON.stringify(ephemeralUser)
    })
    if (!response.ok) {
        let error
        if (response.status == 401) {
            // unauthorized
            error = new Error('Unauthorized: Please login from the old environment.')
            error.code = "401"
        }
        else {
            error = new Error(`Failed to fetch users for Business Entity ID: ${businessEntityId}. Status: ${response.statusText}`)
            error.code = response.status
        }
        throw error
    }
    return await response.json()
}

/****************************************************************************
 * 
 * Roles
 * 
 ***************************************************************************/

export const fetchEphemeralRole = async (businessEntityId, config, accessToken) => {
    const url = `${config.securityServiceBaseUrl}/objectRoleMappings?objectId=1&objectTypeId=7`
    const response = await fetch(url, {
        method: "GET",
        referrerPolicy: "no-referrer-when-downgrade",
        headers: {
            "Content-Type": "application/json",
            'Authorization': `Token token="${accessToken}"`,
            'Shift4-Rest-Request-Envelope-Version': '1',
            'Shift4-Rest-Response-Envelope-Version': '1'
        }
    })
    if (!response.ok) {
        let error
        if (response.status == 401) {
            // unauthorized
            error = new Error('Unauthorized: Please login from the old environment.')
            error.code = "401"
        }
        else {
            error = new Error(`Failed to fetch users for Business Entity ID: ${businessEntityId}. Status: ${response.statusText}`)
            error.code = response.status
        }
        throw error
    }
    return await response.json()
}


/****************************************************************************
 * 
 * User manifest
 * 
 ***************************************************************************/
export const fetchUserManifestsByAccessToken = async (businessEntityId, config, accessToken) => {
    const url = `${config.securityServiceBaseUrl}/userManifests?accessToken=${accessToken}`
    const response = await fetch(url, {
        method: "GET",
        referrerPolicy: "no-referrer-when-downgrade",
        headers: {
            "Content-Type": "application/json",
            'Authorization': `Token token="${accessToken}"`,
            'Shift4-Rest-Request-Envelope-Version': '1',
            'Shift4-Rest-Response-Envelope-Version': '1'
        }
    })
    if (!response.ok) {
        let error
        if (response.status == 401) {
            // unauthorized
            error = new Error('Unauthorized: Please login from the old environment.')
            error.code = "401"
        }
        else {
            error = new Error(`Failed to fetch users for Business Entity ID: ${businessEntityId}. Status: ${response.statusText}`)
            error.code = response.status
        }
        throw error
    }
    return await response.json().then(c => c.userManifest[0])
}



export const fetchUserPermissionMasks = async (userId, config, accessToken) => {
    const url = `${config.securityServiceBaseUrl}/users/${userId}/permissionMasks`
    const response = await fetch(url, {
        method: "GET",
        referrerPolicy: "no-referrer-when-downgrade",
        headers: {
            "Content-Type": "application/json",
            'Authorization': `Token token="${accessToken}"`,
            'Shift4-Rest-Request-Envelope-Version': '1',
            'Shift4-Rest-Response-Envelope-Version': '1'
        }
    })
    if (!response.ok) {
        let error
        if (response.status == 401) {
            // unauthorized
            error = new Error('Unauthorized: Please login from the old environment.')
            error.code = "401"
        }
        else {
            error = new Error(`Failed to fetch permission mask. Status: ${response.statusText}`)
            error.code = response.status
        }
        throw error
    }
    return await response.json().then(c => c.permissionMask)
}

export const fetchPermissions = async (config, accessToken) => {
    const url = `${config.securityServiceBaseUrl}/permissions`
    const response = await fetch(url, {
        method: "GET",
        referrerPolicy: "no-referrer-when-downgrade",
        headers: {
            "Content-Type": "application/json",
            'Authorization': `Token token="${accessToken}"`,
            'Shift4-Rest-Request-Envelope-Version': '1',
            'Shift4-Rest-Response-Envelope-Version': '1'
        }
    })
    if (!response.ok) {
        let error
        if (response.status == 401) {
            // unauthorized
            error = new Error('Unauthorized: Please login from the old environment.')
            error.code = "401"
        }
        else {
            error = new Error(`Failed to fetch permissions. Status: ${response.statusText}`)
            error.code = response.status
        }
        throw error
    }
    return await response.json().then(c => c.permission)
}

export const fetchAccessTokenValidity = async(config, authCode) => {

    try {
        const url = `${config.ssoServiceBaseUrl}/accessTokens/${authCode}`
        const response = await fetch(url, {
            "Content-Type": "application/json",
        })
        const accessToken = await response.json().then(obj => obj)
        return accessToken
    } catch (error) {
        return null
    }

}

export const fetchAccessCodeFromAuthCode = async (config, authCode) => {
    const url = `${config.ssoServiceBaseUrl}/accessTokens`
    const response = await fetch(url, {
        method: "POST",
        referrerPolicy: "no-referrer-when-downgrade",
        headers: {
            "Content-Type": "application/json",
            'ClientId': '4',
            'Shift4-Rest-Request-Envelope-Version': '1',
            'Shift4-Rest-Response-Envelope-Version': '1'
        },
        body: JSON.stringify({
            "authenticationCode": `${authCode}`,
            "applicationId": 4,
            "expires": null,
            "token": null
        })
    })
    if (!response.ok) {
        let error
        if (response.status == 401) {
            // unauthorized
            error = new Error('Unauthorized: Please login from the old environment.')
            error.code = "401"
        }
        else {
            error = new Error(`Failed to fetch accessToken. Status: ${response.statusText}`)
            error.code = response.status
        }
        throw error
    }
    const accessToken = await response.json().then(obj => obj.accessToken)
    if (print) {
        console.log("Returning access token:")
        console.log(accessToken)
    }
    return accessToken
}

export const submitFormData = async (form, callback_success=null, callback_error=null) => {
    const url = form.action
    let print = true
    const response = await fetch(url, {
        method: form.method,
        cache: "no-cache",
        mode: "cors",
        headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            'Authorization': `Token token="${accessToken}"`
        },
        body: new FormData(form)
    })
    if (!response.ok) {
        let error
        if (response.status == 401) {
            // unauthorized
            error = new Error('Unauthorized: Please login from the old environment.')
            error.code = "401"
        }
        else {
            error = new Error(`Failed to submit form data`)
            error.code = response.status
        }
        if (callback_error) {
            callback_error(response, error)
        }
        throw error
    }
    else {
        const rt = await response.json()
        if (print) {
            console.log("Form data successfully submitted")
        }
        if (callback_success) {
            callback_success(rt)
        }
        return rt
    }
}

export const submitLogout = async (config) => {
    const url = `${config.ssoBaseUrl}/logout?clientid=4&redirectToReferer=true`
    let print = true
    const response = await fetch(url, {
        method: "POST",
        cache: "no-cache",
        mode: "cors",
        redirect: "manual",
        headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            'Authorization': `Token token="${accessToken}"`
        },
        body: 'logout=logout'
    })
    if (!response.ok) {
        let error
        if (response.status == 401) {
            // unauthorized
            error = new Error('Unauthorized: Please login from the old environment.')
            error.code = "401"
        }
        else {
            error = new Error(`Failed to send logout`)
            error.code = response.status
        }
        throw error
    }
    else if (print) {
        console.log("Logout form successfully submitted")
    }
    // return the redirected url in case the caller wants to follow
    return response.url
}